import type { ComponentChildren } from "preact";
import { cx } from "./lib/cx.ts";

/** The raised container for rows. Rule: one list per group of like things; rows are separated by divider lines, never by individual cards. */
export function List({ children, class: className, ...rest }: { children?: ComponentChildren; class?: string; "aria-hidden"?: boolean | "true" }) {
  return (
    <div {...rest} class={cx("ui-list", className)}>
      {children}
    </div>
  );
}

/** Rows of grey placeholder bars standing in while a list loads. */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} class="ui-skeleton" />
      ))}
    </div>
  );
}

export interface ListRowProps {
  /** The strong line. Truncates rather than wrapping. */
  name: ComponentChildren;
  /** A quieter second line. Truncates rather than wrapping. */
  sub?: ComponentChildren;
  /** Muted, tabular cells between the name and the right slot. */
  columns?: ComponentChildren[];
  /** A right-aligned figure, such as "3 days ago". */
  right?: ComponentChildren;
  /** Makes the whole row a link (a stretched link on the name). */
  href?: string;
  /** Extra links that stay independently clickable above a linked row. Pass anchors or buttons, never a wrapping link. */
  aside?: ComponentChildren;
  class?: string;
}

/**
 * One row of a List: name (and sub line) on the left, optional columns and a
 * right slot. Rule: when `href` is set the whole row is the click target via
 * a stretched link, and `aside` links are siblings layered above it, so
 * anchors are never nested.
 */
export function ListRow({ name, sub, columns, right, href, aside, class: className }: ListRowProps) {
  return (
    <div class={cx("ui-row", href !== undefined && "ui-row--link", className)}>
      <div class="ui-row__main">
        {href !== undefined ? (
          <a class="ui-row__name" href={href}>
            {name}
          </a>
        ) : (
          <span class="ui-row__name">{name}</span>
        )}
        {sub !== undefined && <span class="ui-row__sub">{sub}</span>}
      </div>
      {columns !== undefined && columns.length > 0 && (
        <div class="ui-row__cols">
          {columns.map((c, i) => (
            <span key={i}>{c}</span>
          ))}
        </div>
      )}
      {right !== undefined && <div class="ui-row__right">{right}</div>}
      {aside !== undefined && <div class="ui-row__aside">{aside}</div>}
    </div>
  );
}
